import {
  Box,
  Divider,
  Grid,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  TextField
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { currencyMapping } from '../../utils/currencyData';
import { getCartItems } from '../cart/ShoppingCart';

import { useNavigate } from 'react-router-dom';
import RestClient from '../../api/axiosInterceptor';
import { createOrder } from '../../redux/actions/medication/drugs/orders';
import DoorDelivery from '../../components/checkout/DoorDelivery.jsx';
import PropTypes from 'prop-types';
import axios from 'axios';

function DeliveryDetails ({ onNext }) {
  const user = useSelector((state) => state.auth?.user);
  const isAuthenticated =
		useSelector((state) => state.auth?.isAuthenticated) ||
		localStorage.getItem('isAuthenticated') === 'true';
  const [deliveryDetails, setDeliveryDetails] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState(() => {
    return localStorage.getItem('selectedDeliveryMethod') || 'standard';
  });

  const [convertedDeliveryPrices, setConvertedDeliveryPrices] = useState({
    standard: { price: null, currency: null },
    express: { price: null, currency: null }
  });
  const [isConverting, setIsConverting] = useState(false);
  const [convertedCartTotal, setConvertedCartTotal] = useState(null);
  const [isCartTotalConverting, setIsCartTotalConverting] = useState(false);
  const cart = useSelector((state) => state.cart.cart);
  const [orderItems, setOrderItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(() => {
    // Load saved data from localStorage on mount
    const savedData = localStorage.getItem('deliveryFormData');
    return savedData
      ? JSON.parse(savedData)
      : {
          age: '',
          name: '',
          email: '',
          phone_number: '',
          shipping_address: '',
          building: '',
          instructions: ''
        };
  });

  // Update formData when localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const savedData = localStorage.getItem('deliveryFormData');
      if (savedData) {
        setFormData(JSON.parse(savedData));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    console.log('Updated form data:', formData);
  }, [formData]);

  useEffect(() => {
    if (formData.email) {
      const total = calculateGrandTotal();
      const deliveryDataWithTotal = {
        ...formData,
        totalAmount: total
      };
      localStorage.setItem(
        'deliveryFormData',
        JSON.stringify(deliveryDataWithTotal)
      );
    }
  }, [formData, calculateGrandTotal]);

  // Add constants for choices
  const DELIVERY_CHOICES = {
    standard: 'Door Delivery',
    express: 'Store Pickup'
  };

  const URGENCY_CHOICES = {
    standard: 'Under 6 hours',
    express: 'Under 2 hours'
  };

  const getCurrencySymbol = (currencyCode) => {
    const country = Object.entries(currencyMapping).find(
      ([_, data]) => data.code === currencyCode
    );
    return country ? country[1].symbol : currencyCode;
  };

  const formatPrice = (price, currency) => {
    const symbol = getCurrencySymbol(currency);
    return `${symbol}${price}`;
  };

  const convertDeliveryPrice = async (type, price, currency) => {
    setIsConverting(true);
    try {
      const response = await fetch('/medication/drug/convert_currency/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ price, currency })
      });
      const data = await response.json();
      if (data.converted_price) {
        setConvertedDeliveryPrices((prev) => ({
          ...prev,
          [type]: {
            price: data.converted_price,
            currency: data.user_currency,
            symbol: getCurrencySymbol(data.user_currency)
          }
        }));
      }
    } catch (error) {
      console.error(`${type} price conversion failed:`, error);
    } finally {
      setIsConverting(false);
    }
  };

  useEffect(() => {
    const convertPrices = async () => {
      try {
        await convertDeliveryPrice('standard', 500, 'KES');
        await convertDeliveryPrice('express', 1000, 'KES');
      } catch (error) {
        console.error('Failed to convert prices:', error);
      }
    };
    convertPrices();
  }, []);

  useEffect(() => {
    // Listen for storage changes from other pages
    const handleStorageChange = (e) => {
      if (e.key === 'selectedDeliveryMethod') {
        setDeliveryMethod(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Conversion function
  const convertCartTotal = async () => {
    if (!cart.length) return;

    setIsCartTotalConverting(true);
    try {
      const total = cart.reduce(
        (sum, item) => sum + item.price * item.count,
        0
      );

      const response = await fetch('/medication/drug/convert_currency/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          price: total,
          currency: cart[0]?.currency || 'USD'
        })
      });

      const data = await response.json();
      if (data.converted_price) {
        setConvertedCartTotal({
          price: data.converted_price,
          currency: data.user_currency
        });
      }
    } catch (error) {
      console.error('Cart total conversion failed:', error);
    } finally {
      setIsCartTotalConverting(false);
    }
  };

  // Trigger conversion on cart change
  useEffect(() => {
    if (cart.length > 0) {
      convertCartTotal();
    }
  }, [cart]);

  const deliveryOptions = {
    standard: {
      price: isConverting
        ? '...'
        : formatPrice(
          convertedDeliveryPrices.standard?.price || 500,
          convertedDeliveryPrices.standard?.currency || 'KES'
        ),
      label: 'Standard Delivery (3-5 days)'
    },
    express: {
      price: isConverting
        ? '...'
        : formatPrice(
          convertedDeliveryPrices.express?.price || 1000,
          convertedDeliveryPrices.express?.currency || 'KES'
        ),
      label: 'Express Delivery (1-2 days)'
    }
  };

  const getTotalItemCount = () => {
    return cart.reduce((total, item) => total + item.count, 0);
  };

  const calculateTotalPrice = () => {
    if (isCartTotalConverting) return 'Converting...';

    if (convertedCartTotal?.price) {
      const symbol = getCurrencySymbol(convertedCartTotal.currency);
      return `${symbol}${convertedCartTotal.price.toLocaleString()}`;
    }

    const total = cart.reduce((sum, item) => sum + item.price * item.count, 0);
    const symbol = getCurrencySymbol(cart[0]?.currency || 'USD');
    return `${symbol}${total.toLocaleString()}`;
  };

  const calculateGrandTotal = () => {
    // Handle loading states
    if (isConverting || isCartTotalConverting) {
      return 'Converting...';
    }

    // Get converted cart total or fallback
    const cartTotal =
			convertedCartTotal?.price ||
			cart.reduce((total, item) => total + item.price * item.count, 0);

    // Get converted shipping cost or fallback
    const shippingCost =
			convertedDeliveryPrices[deliveryMethod]?.price ||
			(deliveryMethod === 'standard' ? 500 : 1000);

    // Calculate grand total
    const grandTotal = parseFloat(cartTotal) + parseFloat(shippingCost);

    // Use converted currency or fallback
    const currency =
			convertedCartTotal?.currency ||
			convertedDeliveryPrices[deliveryMethod]?.currency ||
			cart[0]?.currency ||
			'USD';

    const symbol = getCurrencySymbol(currency);

    return `${symbol}${grandTotal.toLocaleString()}`;
  };

  const shippingCost = deliveryOptions[deliveryMethod].price;

  // Load order items
  useEffect(() => {
    const storedCart = localStorage.getItem('cartItems');
    if (storedCart) {
      try {
        const parsedCart = JSON.parse(storedCart);
        const formattedItems = parsedCart.map((item) => ({
          drug_id: item.id,
          quantity: item.quantity,
          price: item.price
        }));
        setOrderItems(formattedItems);
      } catch (err) {
        console.error('Error parsing cart items:', err);
        setOrderItems([]);
      }
    }
  }, []);

  useEffect(() => {
    console.log('DeliveryDetails - Auth state:', isAuthenticated);
    console.log('DeliveryDetails - Order items:', orderItems);
  }, [isAuthenticated, orderItems]);

  // Get CSRF token from cookies
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  // Order submission function
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('handleSubmit function called');

    // Authentication check
    if (!isAuthenticated) {
      console.error('User not authenticated, redirecting to login');
      localStorage.setItem('checkoutInProgress', 'true');
      localStorage.setItem('checkoutTargetStep', '1');
      localStorage.setItem(
        'pendingOrderData',
        JSON.stringify({ deliveryMethod, formData })
      );
      navigate('/app/patient_login');
      return;
    }

    // Cart validation
    if (!cart || cart.length === 0) {
      setErrors({ cart: ['Cart is empty'] });
      return;
    }

    try {
      // Debug log cart structure
      console.log('Cart items:', cart);

      // More robust item mapping
      const orderItems = cart.map((item) => {
        // Extract drug ID using multiple possible paths
        const drugId = item.id || item.drug?.id || item.drug_id;

        console.log('Processing item:', item.name, 'Drug ID:', drugId);

        if (!drugId) {
          console.error('Item structure:', item);
          throw new Error(`Missing drug ID for item: ${item.name}`);
        }

        return {
          drug_id: drugId,
          quantity: item.count || item.quantity || 1,
          price_per_unit: item.price || item.unit_price || 0
        };
      });

      // Debug log mapped items
      console.log('Mapped order items:', orderItems);

      const totalAmount = calculateGrandTotal();
      const orderData = {
        total_price: parseFloat(totalAmount.replace(/[^0-9.]/g, '')),
        delivery_method: DELIVERY_CHOICES[deliveryMethod],
        urgency_period: URGENCY_CHOICES[deliveryMethod],
        is_urgent: deliveryMethod === 'express',
        age: parseInt(formData.age, 10) || 0,
        items: orderItems,
        shipping_address: formData.shipping_address,
        phone_number: formData.phone_number,
        email: formData.email,
        symptoms: formData.symptoms
      };

      // Debug log final order data
      console.log('Final order payload:', JSON.stringify(orderData, null, 2));

      console.log('Attempting to send order to database:', orderData);

      // Get fresh credentials
      const credentials = localStorage.getItem('patientCredentials');
      console.log(
        'Using credentials:',
        credentials ? credentials.substring(0, 10) + '...' : 'NONE'
      );

      if (!credentials) {
        console.error('No credentials found, redirecting to login');
        localStorage.setItem('checkoutInProgress', 'true');
        localStorage.setItem('checkoutTargetStep', '3');
        localStorage.setItem(
          'pendingOrderData',
          JSON.stringify({
            deliveryMethod,
            formData,
            orderItems
          })
        );
        navigate('/app/patient_login');
        return;
      }

      // Debugging output
      console.log('Making API call with credentials');

      // Get the CSRF token using your existing getCookie function
      const csrftoken = getCookie('csrftoken');
      console.log('CSRF token found:', csrftoken ? 'YES' : 'NO');

      // Make the request
      const response = await RestClient.post('/medication/orders/', orderData, {
        headers: {
          Authorization: `Basic ${credentials}`,
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken || ''
        },
        withCredentials: true
      });

      console.log('Order submission response:', response.data);

      if (response.data.public_id) {
        console.log(
          'Order successfully created with ID:',
          response.data.public_id
        );
        localStorage.setItem('orderId', response.data.public_id);
        localStorage.setItem(
          'orderDetails',
          JSON.stringify({
            ...orderData,
            totalAmount
          })
        );

        // Clear auth-related flags
        localStorage.removeItem('checkoutInProgress');
        localStorage.removeItem('checkoutTargetStep');
        localStorage.removeItem('pendingOrderData');

        onNext();
      } else {
        console.error('No public_id in response:', response.data);
        setErrors({ general: ['Order was not created properly'] });
      }
    } catch (error) {
      console.error('Order creation failed:', error);
      console.error('Error response:', error.response?.data);

      // Specific handling for auth errors
      if (error.response?.status === 403) {
        console.error('Authentication failed - invalid or expired credentials');
        // Remove the correct storage items
        localStorage.removeItem('patientCredentials');
        localStorage.removeItem('isAuthenticated');
        navigate('/app/patient_login');
        return;
      }

      // Handle specific error cases
      if (
        error.response?.status === 404 &&
				error.response?.data?.detail?.includes('Patient data')
      ) {
        // Missing patient data case
        console.error('Patient profile not found');
        localStorage.setItem('checkoutInProgress', 'true');
        localStorage.setItem('checkoutTargetStep', '1');
        navigate('/app/patient_dashboard');
        return;
      }

      setErrors(
        error.response?.data || { general: ['Failed to create order'] }
      );
    }
  };
  return (
    <Box
      sx={{
			  width: '100%',
			  typography: 'body1',
			  marginTop: '3rem',
			  backgroundColor: '#1e1e1e',
			  color: 'white',
			  padding: '2rem',
			  borderRadius: '8px'
      }}
    >
      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Typography variant='h3' fontWeight='bold' mb={3}>
            Delivery Method
          </Typography>
          <RadioGroup
            value={deliveryMethod}
            onChange={(e) => {
						  const newValue = e.target.value;
						  setDeliveryMethod(newValue);
						  localStorage.setItem('selectedDeliveryMethod', newValue);
						  // Dispatch storage event for other pages
						  window.dispatchEvent(
						    new StorageEvent('storage', {
						      key: 'selectedDeliveryMethod',
						      newValue
                })
						  );
            }}
          >
            {Object.entries(deliveryOptions).map(([method, details]) => (
              <Box
                key={method}
                sx={{
								  mb: 2,
								  p: 2,
								  border: '1px solid rgba(255,255,255,0.2)',
								  borderRadius: 1
                }}
              >
                <FormControlLabel
                  value={method}
                  control={<Radio sx={{ color: 'white' }} />}
                  label={
                    <Box>
                    <Typography>{details.label}</Typography>
                    <Typography color='grey.400'>{details.price}</Typography>
                  </Box>
									}
                />
              </Box>
            ))}
          </RadioGroup>
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography variant='h3' fontWeight='bold' mb={3}>
            Order Summary
          </Typography>
          <Box display='flex' justifyContent='space-between' mb={2}>
            <Typography fontSize='20px'>
              Items ({getTotalItemCount()})
            </Typography>
            <Typography fontWeight='bold'>
              <span>{calculateTotalPrice()}</span>
            </Typography>
          </Box>
          <Box display='flex' justifyContent='space-between' mb={2}>
            <Typography fontSize='20px'>Shipping</Typography>
            <Typography fontWeight='bold'>{shippingCost}</Typography>
          </Box>
          <Divider sx={{ bgcolor: 'rgba(255,255,255,0.2)', my: 2 }} />
          <Box display='flex' justifyContent='space-between' mb={2}>
            <Typography fontSize='20px' fontWeight='bold'>
              Total
            </Typography>
            <Typography fontSize='20px' fontWeight='bold'>
              <span>{calculateGrandTotal()}</span>
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 4 }}>
        <Box>
          {formData.email && (
            <Box sx={{ mt: 3, p: 2, border: '1px solid', borderRadius: 1 }}>
              <Typography variant='h6' gutterBottom>
                Delivery Summary
              </Typography>
              <Typography>Name: {formData.name}</Typography>
              <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                Email: {formData.email}
                <Box
                  component='span'
                  sx={{
									  display: 'inline-flex',
									  alignItems: 'center',
									  ml: 1,
									  color: isAuthenticated ? 'green' : 'orange',
									  fontSize: '0.85em',
									  border: 1,
									  borderColor: isAuthenticated ? 'green' : 'orange',
									  borderRadius: '4px',
									  px: 0.8,
									  py: 0.2
                  }}
                >
                  {isAuthenticated
									  ? '✓ Verified Account'
									  : '⚠️ Sign in required'}
                </Box>
              </Typography>
              <Typography>Age: {formData.age}</Typography>
              <Typography>Phone: {formData.phone_number}</Typography>
              <Typography>Address: {formData.shipping_address}</Typography>
              <Typography>Building: {formData.building}</Typography>
              {formData.instructions && (
                <Typography>Instructions: {formData.instructions}</Typography>
              )}
              <Box sx={{ mt: 2, borderTop: '1px solid', pt: 2 }}>
                <Typography fontSize='20px' fontWeight='bold'>
                  Total Amount: <span>{calculateGrandTotal()}</span>
                </Typography>
                <Button
                  variant='contained'
                  fullWidth
                  onClick={handleSubmit}
                  sx={{
									  mt: 2,
									  backgroundColor: '#018749',
									  '&:hover': {
									    backgroundColor: '#016749'
                    },
                  }}
                >
                  Proceed to Payment
                </Button>
              </Box>
            </Box>
          )}
        </Box>
        {errors.items && (
          <Grid item xs={12}>
            <Typography color='error'>
              {errors.items[0]?.drug_id?.[0]}
            </Typography>
          </Grid>
        )}

        {(errors.delivery_method || errors.urgency_period) && (
          <Grid item xs={12}>
            <Typography color='error'>
              {errors.delivery_method?.[0] || errors.urgency_period?.[0]}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

DeliveryDetails.propTypes = {
  calculateGrandTotal: PropTypes.func.isRequired
};

DeliveryDetails.propTypes = {
  onNext: PropTypes.func.isRequired
};

export default DeliveryDetails;
